import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, DollarSign, TrendingDown, Filter, Search } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RealCREProperties = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    state: '',
    city: '',
    property_type: '',
    is_distressed: ''
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStatistics();
    fetchProperties();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cre/cre-statistics`);
      if (response.data.success) {
        setStatistics(response.data.statistics);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.state) params.append('state', filters.state);
      if (filters.city) params.append('city', filters.city);
      if (filters.property_type) params.append('property_type', filters.property_type);
      if (filters.is_distressed) params.append('is_distressed', filters.is_distressed);
      
      const response = await axios.get(`${BACKEND_URL}/api/cre/cre-properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchProperties();
  };

  const filteredProperties = properties.filter(prop => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      prop.name.toLowerCase().includes(query) ||
      prop.address.toLowerCase().includes(query) ||
      prop.city.toLowerCase().includes(query) ||
      prop.submarket.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-[#2A2A33] mb-2">Real CRE Properties</h1>
            <p className="text-[#6B6B76] text-lg">102 properties from Florida, Colorado & New York City</p>
          </div>

          {/* Statistics Dashboard */}
          {statistics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-white border-gray-200 p-6 shadow-sm">
                <div className="text-sm text-[#6B6B76] mb-1">Total Properties</div>
                <div className="text-3xl font-bold text-[#2A2A33]">{statistics.total_properties}</div>
                <div className="text-xs text-[#6B6B76] mt-1">
                  {statistics.distressed} distressed ({statistics.distressed_percentage}%)
                </div>
              </Card>
              <Card className="bg-white border-gray-200 p-6 shadow-sm">
                <div className="text-sm text-[#6B6B76] mb-1">Total Value</div>
                <div className="text-3xl font-bold text-[#2A2A33]">
                  ${(statistics.aggregates.total_value / 1000000000).toFixed(2)}B
                </div>
                <div className="text-xs text-[#6B6B76] mt-1">
                  Avg: ${(statistics.aggregates.avg_value / 1000000).toFixed(1)}M
                </div>
              </Card>
              <Card className="bg-white border-gray-200 p-6 shadow-sm">
                <div className="text-sm text-[#6B6B76] mb-1">Avg Cap Rate</div>
                <div className="text-3xl font-bold text-[#2A2A33]">{statistics.aggregates.avg_cap_rate}%</div>
                <div className="text-xs text-[#6B6B76] mt-1">
                  Avg Occupancy: {statistics.aggregates.avg_occupancy}%
                </div>
              </Card>
              <Card className="bg-white border-gray-200 p-6 shadow-sm">
                <div className="text-sm text-[#6B6B76] mb-1">Avg LTV</div>
                <div className="text-3xl font-bold text-[#2A2A33]">{statistics.aggregates.avg_ltv}%</div>
                <div className="text-xs text-[#6B6B76] mt-1">
                  Across all properties
                </div>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="bg-white border-gray-200 p-6 mb-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="text-sm text-[#6B6B76] mb-2 block">State</label>
                <select 
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                  className="w-full bg-white border border-gray-300 text-[#2A2A33] rounded-lg px-3 py-2 focus:border-[#006AFF] focus:ring-1 focus:ring-[#006AFF] outline-none"
                >
                  <option value="">All States</option>
                  <option value="FL">Florida</option>
                  <option value="CO">Colorado</option>
                  <option value="NY">New York</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-[#6B6B76] mb-2 block">Property Type</label>
                <select 
                  value={filters.property_type}
                  onChange={(e) => handleFilterChange('property_type', e.target.value)}
                  className="w-full bg-white border border-gray-300 text-[#2A2A33] rounded-lg px-3 py-2 focus:border-[#006AFF] focus:ring-1 focus:ring-[#006AFF] outline-none"
                >
                  <option value="">All Types</option>
                  <option value="office">Office</option>
                  <option value="retail">Retail</option>
                  <option value="industrial">Industrial</option>
                  <option value="multifamily">Multifamily</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-[#6B6B76] mb-2 block">Status</label>
                <select 
                  value={filters.is_distressed}
                  onChange={(e) => handleFilterChange('is_distressed', e.target.value)}
                  className="w-full bg-white border border-gray-300 text-[#2A2A33] rounded-lg px-3 py-2 focus:border-[#006AFF] focus:ring-1 focus:ring-[#006AFF] outline-none"
                >
                  <option value="">All Status</option>
                  <option value="true">Distressed Only</option>
                  <option value="false">Performing Only</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-[#6B6B76] mb-2 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B6B76] h-4 w-4" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-white border-gray-300 text-[#2A2A33] focus:border-[#006AFF]"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={applyFilters}
                  className="w-full bg-[#006AFF] hover:bg-[#0055CC] text-white"
                >
                  <Filter className="mr-2 h-4 w-4" />
                  Apply Filters
                </Button>
              </div>
            </div>
          </Card>

          {/* Properties Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="text-[#6B6B76]">Loading properties...</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProperties.map(property => (
                <Card 
                  key={property.id}
                  onClick={() => navigate(`/cre-property/${property.id}`)}
                  className="bg-white border-gray-200 overflow-hidden hover:shadow-lg hover:border-[#006AFF] transition-all cursor-pointer group"
                >
                  {/* Property Photo */}
                  {property.photo_url && (
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={property.photo_url}
                        alt={property.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      {property.is_distressed && (
                        <span className="absolute top-3 left-3 bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full flex items-center">
                          <TrendingDown className="h-3 w-3 mr-1" />
                          Distressed
                        </span>
                      )}
                      {property.source === 'Brookfield Properties' && (
                        <span className="absolute top-3 right-3 bg-[#006AFF] text-white text-xs px-2 py-1 rounded-full">
                          Brookfield
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="text-xs text-[#006AFF] font-medium mb-1">
                          {property.property_type.toUpperCase()}
                        </div>
                        <h3 className="text-lg font-bold text-[#2A2A33]">{property.name}</h3>
                      </div>
                      {!property.photo_url && property.is_distressed && (
                        <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full flex items-center">
                          <TrendingDown className="h-3 w-3 mr-1" />
                          Distressed
                        </span>
                      )}
                    </div>

                    <div className="flex items-start text-[#6B6B76] text-sm mb-4">
                      <MapPin className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{property.address}</span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B6B76]">Value:</span>
                        <span className="text-[#2A2A33] font-semibold">
                          ${(property.property_value / 1000000).toFixed(1)}M
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B6B76]">Size:</span>
                        <span className="text-[#2A2A33] font-semibold">
                          {property.size_sqft.toLocaleString()} SF
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B6B76]">Price/SF:</span>
                        <span className="text-[#2A2A33] font-semibold">
                          ${property.price_per_sqft.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B6B76]">Loan:</span>
                        <span className="text-[#2A2A33] font-semibold">
                          ${(property.loan_amount / 1000000).toFixed(1)}M
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B6B76]">LTV:</span>
                        <span className="text-[#2A2A33] font-semibold">{property.ltv_ratio}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B6B76]">Rate:</span>
                        <span className="text-[#2A2A33] font-semibold">{property.interest_rate}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B6B76]">Cap Rate:</span>
                        <span className="text-[#2A2A33] font-semibold">{property.cap_rate}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B6B76]">Occupancy:</span>
                        <span className="text-[#2A2A33] font-semibold">{property.occupancy_rate}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B6B76]">NOI:</span>
                        <span className="text-[#2A2A33] font-semibold">
                          ${(property.noi / 1000).toFixed(0)}K
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="text-xs text-[#6B6B76] mb-2">
                        Status: <span className={property.is_distressed ? 'text-red-500' : 'text-green-600'}>
                          {property.status}
                        </span>
                      </div>
                      <div className="text-xs text-[#6B6B76]">
                        Built: {property.year_built} | {property.submarket}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {filteredProperties.length === 0 && !loading && (
            <div className="text-center py-12">
              <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-[#6B6B76] text-lg">No properties found matching your filters.</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RealCREProperties;
